import React, { useState, useEffect, useContext } from 'react';
import api from '../../../services/api';
import { AuthContext } from '../../../context/AuthContext';
import RequestHistoryModal from '../../../components/RequestHistoryModal';
import RequestDetailsModal from '../../../components/RequestDetailsModal';

const WFH_OD = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('WFH'); // 'WFH' or 'OD'

  const [wfhRequests, setWfhRequests] = useState([]);
  const [odRequests, setOdRequests] = useState([]);
  const [wfhBalance, setWfhBalance] = useState(0);

  const [showForm, setShowForm] = useState(false);
  
  // WFH Form state
  const [wfhStartDate, setWfhStartDate] = useState('');
  const [wfhEndDate, setWfhEndDate] = useState('');
  const [wfhReason, setWfhReason] = useState('');

  // OD Form state
  const [odDate, setOdDate] = useState('');
  const [odReason, setOdReason] = useState('');

  const [error, setError] = useState('');
  
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState([]);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchData = async () => {
    try {
      const [wfhRes, odRes, profileRes] = await Promise.all([
        api.get('/attendance/wfh/me'),
        api.get('/attendance/od/me'),
        api.get(`/employees/${user.id}`)
      ]);
      setWfhRequests(wfhRes.data);
      setOdRequests(odRes.data);
      setWfhBalance(profileRes.data.wfhBalance || 0);
    } catch (err) {
      console.error('Failed to fetch data', err);
    }
  };

  useEffect(() => {
    if (user?.id) fetchData();
  }, [user]);

  const handleWfhSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/attendance/wfh', { startDate: wfhStartDate, endDate: wfhEndDate, reason: wfhReason });
      setShowForm(false);
      setWfhStartDate('');
      setWfhEndDate('');
      setWfhReason('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit WFH request');
    }
  };

  const handleOdSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/attendance/od', { date: odDate, reason: odReason });
      setShowForm(false);
      setOdDate('');
      setOdReason('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit OD request');
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto pb-10 w-full min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">WFH & On-Duty Requests</h1>
        <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-200 px-3.5 py-2 rounded-xl flex items-center justify-between sm:justify-start gap-3 shadow-sm w-full sm:w-auto">
          <span className="text-purple-800 font-medium text-sm sm:text-base">Available WFH Balance:</span>
          <div className="flex items-center gap-2 bg-white/50 px-2 py-1 rounded-lg border border-purple-100">
            <span className="bg-purple-600 text-white font-bold px-2.5 py-0.5 rounded-md shadow-inner text-base sm:text-lg">
              {wfhBalance} <span className="text-xs font-normal opacity-80">Left</span>
            </span>
          </div>
        </div>
      </div>

      <div className="mb-6 overflow-x-auto pb-1">
        <nav className="flex space-x-2 sm:space-x-4 border-b border-gray-200 pb-2 min-w-max">
          <button
            onClick={() => { setActiveTab('WFH'); setShowForm(false); setError(''); }}
            className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm rounded-t-lg transition-colors whitespace-nowrap
              ${activeTab === 'WFH' ? 'bg-purple-100 text-purple-800 border-b-2 border-purple-600' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            Work From Home (WFH)
          </button>
          <button
            onClick={() => { setActiveTab('OD'); setShowForm(false); setError(''); }}
            className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm rounded-t-lg transition-colors whitespace-nowrap
              ${activeTab === 'OD' ? 'bg-blue-100 text-blue-800 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            On-Duty / Client Meetings (OD)
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded shadow-sm">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-end mb-6">
        <button
          onClick={() => setShowForm(!showForm)}
          className={`${activeTab === 'WFH' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 sm:px-5 py-2.5 rounded-xl shadow-md transition-all font-medium text-sm sm:text-base flex items-center justify-center gap-2 w-full sm:w-auto`}
        >
          {showForm ? 'Cancel Application' : `+ Apply for ${activeTab}`}
        </button>
      </div>

      {showForm && activeTab === 'WFH' && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow mb-8 border-t-4 border-purple-500">
          <h2 className="text-lg font-bold mb-4">Apply for Work From Home</h2>
          <form onSubmit={handleWfhSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input 
                type="date" required
                value={wfhStartDate}
                onChange={e => setWfhStartDate(e.target.value)}
                min={new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]} 
                className="w-full p-2 border rounded focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input 
                type="date" required
                value={wfhEndDate}
                onChange={e => setWfhEndDate(e.target.value)}
                min={wfhStartDate || new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]} 
                className="w-full p-2 border rounded focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <input 
                type="text" required
                value={wfhReason}
                onChange={e => setWfhReason(e.target.value)}
                className="w-full p-2 border rounded focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div className="col-span-1 sm:col-span-2 text-right">
              <button type="submit" className="bg-purple-600 text-white px-6 py-2.5 rounded-lg hover:bg-purple-700 w-full sm:w-auto font-medium text-sm">Submit Application</button>
            </div>
          </form>
        </div>
      )}

      {showForm && activeTab === 'OD' && (
        <div className="bg-white p-6 rounded-lg shadow mb-8 border-t-4 border-blue-500">
          <h2 className="text-lg font-bold mb-2">Apply for On-Duty (Client Meeting / External Work)</h2>
          <p className="text-sm text-gray-500 mb-4">If approved by your manager, this will mark you as Present for the full day without penalties for being late or outside the office geofence.</p>
          <form onSubmit={handleOdSubmit} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input 
                type="date" required
                value={odDate}
                onChange={e => setOdDate(e.target.value)}
                className="w-full md:w-1/2 p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Meeting Details</label>
              <input 
                type="text" required placeholder="E.g., Client meeting at DEF Corp"
                value={odReason}
                onChange={e => setOdReason(e.target.value)}
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="text-right mt-2">
              <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">Submit Application</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden mb-8 border border-gray-100">
        <RequestHistoryModal 
          isOpen={historyModalOpen} 
          onClose={() => setHistoryModalOpen(false)} 
          history={currentHistory} 
        />
        <RequestDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          request={selectedRequest}
          type={activeTab}
        />
        <div className="px-4 sm:px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{activeTab === 'WFH' ? 'WFH Requests' : 'On-Duty Requests'}</h3>
          <span className="text-xs text-gray-400 block sm:hidden">Swipe sideways to view full table &rarr;</span>
        </div>
        <div className="overflow-x-auto w-full custom-scrollbar">
          <table className="w-full min-w-[580px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date(s)</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                {activeTab === 'WFH' && <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>}
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Track</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(activeTab === 'WFH' ? wfhRequests : odRequests).length === 0 ? (
                <tr><td colSpan={activeTab === 'WFH' ? 5 : 4} className="px-6 py-8 text-center text-gray-500 text-sm">No {activeTab} requests found</td></tr>
              ) : (
                (activeTab === 'WFH' ? wfhRequests : odRequests).map(req => (
                  <tr key={req._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-900 font-medium">
                      {activeTab === 'WFH' ? (
                        <>
                          {new Date(req.startDate).toLocaleDateString()}
                          {req.startDate !== req.endDate && ` - ${new Date(req.endDate).toLocaleDateString()}`}
                        </>
                      ) : (
                        new Date(req.date).toLocaleDateString()
                      )}
                    </td>
                    <td 
                      className="px-4 sm:px-6 py-3.5 text-xs sm:text-sm text-gray-700 max-w-sm whitespace-normal break-words cursor-pointer hover:text-blue-600 transition-colors"
                      title="Click to view full reason"
                      onClick={() => {
                        setSelectedRequest(req);
                        setDetailsModalOpen(true);
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="line-clamp-2">{req.reason}</span>
                        <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${req.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                          req.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {req.status}
                      </span>
                    </td>
                    {activeTab === 'WFH' && (
                      <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                        {req.paymentStatus && req.paymentStatus !== 'PENDING' ? (
                          <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${req.paymentStatus === 'FULL_DAY_PAID' ? 'bg-indigo-100 text-indigo-800' : 
                              req.paymentStatus === 'HALF_DAY_PAID' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'}`}>
                            {req.paymentStatus.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs italic">Pending Approval</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setCurrentHistory(req.history || []);
                          setHistoryModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 font-medium text-xs underline"
                      >
                        Track
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WFH_OD;
